import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  X, 
  Target, 
  TrendingUp, 
  Calendar,
  User,
  Building,
  Phone,
  Mail,
  DollarSign,
  Star,
  FileText,
  Globe,
  Users,
  Award,
  Trophy,
  Activity,
  AlertCircle,
  Clock,
  Briefcase,
  CheckCircle,
  RefreshCw,
  Paperclip,
  Plus,
  Search,
  Link,
  Check,
  Tag,
  HelpCircle
} from 'lucide-react';
import AttachmentServiceV2 from '../../components/Attachments/AttachmentServiceV2';
import TagService from '../../components/Tags/TagService';
import entityRelationService from '../../services/entityRelationService';
import { api } from '../../utils/api';
import EntitySelectionModal from '../../components/EntityManagement/EntitySelectionModal';
import CustomAttributesEditor from '../../components/CustomAttributes/CustomAttributesEditor';
import {
  extractCustomAttributeFields,
  sanitizeAttributePayload
} from '../../components/CustomAttributes/utils';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { clearConfigCache, loadEntityConfig } from '../../utils/entityConfig';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import dataGovernanceService from '../../services/dataGovernanceService';
import { AlertTriangle } from 'lucide-react';
import HelpModal from '../../components/Help/HelpModal';
import { opportunityHelpContent } from '../../components/Help/helpContent/opportunities';
import {
  ValidationSummary,
  FieldError,
  getInputClassName,
  getSelectClassName,
  isEmpty,
  isValidEmail,
  normalizeEmailList,
  isPositiveNumber,
  isInRange,
  scrollToFirstError
} from '../../components/Forms/FormValidation';
import AutocompleteTextField from '../../components/Forms/AutocompleteTextField';
import { getSuggestionConfig } from '../../components/Forms/TextFieldWithSuggestions';

const parseAttributes = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) || {};
    } catch (error) {
      console.warn('OpportunityForm: Failed to parse attributes JSON', error);
      return {};
    }
  }
  return {};
};

// Helper function to convert UTC date to local date string for HTML date input
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    // Create a Date object from the UTC string
    const date = new Date(dateString);
    // Get the local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    // Return in YYYY-MM-DD format for HTML date input
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.warn('OpportunityForm: Failed to format date', error);
    return '';
  }
};

// Helper functions for number formatting in inputs
const formatNumberWithCommas = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  
  // Handle string values (preserving decimals during typing)
  if (typeof value === 'string') {
    const cleanedForParsing = value.replace(/,/g, '');
    
    // If ends with decimal point, format integer part and preserve the decimal
    if (cleanedForParsing.endsWith('.')) {
      const intPart = parseInt(cleanedForParsing.slice(0, -1), 10);
      if (isNaN(intPart)) return value;
      return new Intl.NumberFormat('en-US', { useGrouping: true }).format(intPart) + '.';
    }
    
    // If has decimal, format preserving decimal places (up to 2)
    if (cleanedForParsing.includes('.')) {
      const numValue = parseFloat(cleanedForParsing);
      if (isNaN(numValue)) return value;
      const decimalPart = cleanedForParsing.split('.')[1] || '';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: Math.min(decimalPart.length, 2),
        maximumFractionDigits: 2,
        useGrouping: true
      }).format(numValue);
    }
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(numValue)) return value;
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    useGrouping: true
  }).format(numValue);
};

const parseNumberFromFormatted = (value) => {
  if (!value || value === '') return '';
  // Remove all commas, spaces, and other formatting characters, then parse
  const cleaned = String(value).trim().replace(/[,\s]/g, '');
  if (cleaned === '') return '';
  
  // Preserve trailing decimal point or incomplete decimals during typing
  // This allows users to type "1234." and then continue with decimal digits
  if (/^-?\d+\.$/.test(cleaned) || /^-?\d+\.\d{0,2}$/.test(cleaned)) {
    return cleaned; // Return as string to preserve decimal during input
  }
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? '' : parsed;
};

const OpportunityForm = ({ opportunity: opportunityProp, mode, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const tenantCode =
    user?.selectedTenant?.code ||
    user?.selectedTenant?.tenant_code ||
    user?.tenantCode ||
    null;
  const { config: entityConfig, loading: configLoading, error: configError } = useEntityConfig('opportunity');
  
  // State for loading opportunity data
  const [opportunity, setOpportunity] = useState(opportunityProp);
  const [loadingOpportunity, setLoadingOpportunity] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    OpportunityName: opportunity?.OpportunityName || '',
    Description: opportunity?.Description || '',
    Stage: opportunity?.Stage || 'Qualified',
    Priority: opportunity?.Priority || 'Medium',
    Category: opportunity?.Category || 'New Business',
    
    // Required database fields
    SalespersonID: opportunity?.SalespersonID ? String(opportunity.SalespersonID) : '',
    
    // Customer Information
    CustomerName: opportunity?.CustomerName || '',
    ContactName: opportunity?.ContactName || '',
    ContactEmail: opportunity?.ContactEmail || '',
    ContactPhone: opportunity?.ContactPhone || '',
    
    // Financial Information
    Value: opportunity?.Value || '',
    Currency: opportunity?.Currency || 'THB',
    Probability: opportunity?.Probability || 50,
    
    // Timeline
    ExpectedCloseDate: formatDateForInput(opportunity?.ExpectedCloseDate),
    LastActivityDate: formatDateForInput(opportunity?.LastActivityDate),
    NextFollowUpDate: formatDateForInput(opportunity?.NextFollowUpDate),
    
    // Additional Information
    CompetitorInfo: opportunity?.CompetitorInfo || '',
    Requirements: opportunity?.Requirements || '',
    Notes: opportunity?.Notes || '',
    
    // Sales Information
    NextStepAction: opportunity?.NextStepAction || '',
    
    // Flags
    IsActive: opportunity?.IsActive !== undefined ? opportunity.IsActive : true,
    IsHighValue: opportunity?.IsHighValue || false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [salespersons, setSalespersons] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [entityRelationsUnified, setEntityRelationsUnified] = useState([]);
  const [customAttributeFields, setCustomAttributeFields] = useState([]);
  const [attributes, setAttributes] = useState(() =>
    parseAttributes(opportunity?.Attributes || opportunity?.attributes)
  );
  const [dataGovernancePolicy, setDataGovernancePolicy] = useState(null);
  const [showSalespersonWarning, setShowSalespersonWarning] = useState(false);
  const [originalSalespersonID, setOriginalSalespersonID] = useState(null);
  const [hiddenFields, setHiddenFields] = useState([]);
  // LeadForm-style editing state
  const [relatedEntities, setRelatedEntities] = useState([]); // {type, id, name}[]
  const initialRelationKeysRef = React.useRef(new Set());
  const [entitySelectionModal, setEntitySelectionModal] = useState({ isOpen: false, entityType: null, searchTerm: '', results: [], loading: false, selectedIds: [] });
  const [searchTimeouts, setSearchTimeouts] = useState({});
  const entityTypes = [
    { value: 'Customer', label: t('activities.entityTypes.customer', 'Customer') },
    { value: 'Lead', label: t('activities.entityTypes.lead', 'Lead') },
    { value: 'Activity', label: t('activities.activity', 'Activity') },
    { value: 'SaleNote', label: t('activities.entityTypes.saleNote', 'Sale Note') }
  ];
  
  // Dynamic list values
  const [stageOptions, setStageOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([
    { value: 'THB', label: 'THB (฿)' },
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'JPY', label: 'JPY (¥)' },
  ]);

  // Load opportunity data if in edit mode and opportunity prop is not provided
  React.useEffect(() => {
    const loadOpportunity = async () => {
      // If opportunity prop is provided, use it
      if (opportunityProp) {
        setOpportunity(opportunityProp);
        return;
      }
      
      // If in edit mode and we have an ID from route params, load the opportunity
      if (mode === 'edit' && id) {
        setLoadingOpportunity(true);
        try {
          const response = await api.get(`/opportunities/${id}`);
          const loadedOpportunity = response.opportunity || response;
          setOpportunity(loadedOpportunity);
        } catch (error) {
          console.error('Failed to load opportunity:', error);
          toast.error(t('opportunities.loadError', 'Failed to load opportunity data'));
          // Navigate back on error
          if (onCancel) {
            onCancel();
          } else {
            navigate('/opportunities');
          }
        } finally {
          setLoadingOpportunity(false);
        }
      }
    };
    
    loadOpportunity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id, opportunityProp]);

  // Fetch salespersons and data governance policy on component mount
  React.useEffect(() => {
    const fetchSalespersons = async () => {
      try {
        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch('/api/users/salespersons', {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        if (response.ok) {
          const data = await response.json();
          // Filter to only include users with Salesperson role (exclude Admin-only users)
          const salespersonOnly = (data.salespersons || []).filter(user => {
            const roles = user.Roles || '';
            // Include if user has Salesperson role (may also have Admin, but must have Salesperson)
            return roles.includes('Salesperson');
          });
          setSalespersons(salespersonOnly);
        }
      } catch (error) {
        console.error('Error fetching salespersons:', error);
      }
    };
    
    const fetchDataGovernancePolicy = async () => {
      try {
        const policy = await dataGovernanceService.getPolicy('Opportunity');
        setDataGovernancePolicy(policy);
        console.log('🔍 OpportunityForm: Data governance policy loaded:', policy);
      } catch (error) {
        console.error('Error fetching data governance policy:', error);
      }
    };
    
    fetchSalespersons();
    fetchDataGovernancePolicy();
  }, []);

  // Load selection values from tenant config with global fallback
  React.useEffect(() => {
    const loadSelectionValues = async () => {
      try {
        console.log('🔍 OpportunityForm: Loading selection values from tenant config...');
        const response = await fetch('/api/tenant-selection/opportunity', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const selectionValues = data.selectionValues;
          
          console.log('🔍 OpportunityForm: Received selection values:', selectionValues);
          
          // Update Stage options
          if (selectionValues?.Stage?.values) {
            const stageOpts = selectionValues.Stage.values.map(v => ({
              value: v.value,
              label: v.label
            }));
            setStageOptions(stageOpts);
            console.log('✅ OpportunityForm: Set Stage options:', stageOpts);
          }
          
          // Update Priority options
          if (selectionValues?.Priority?.values) {
            const priorityOpts = selectionValues.Priority.values.map(v => ({
              value: v.value,
              label: v.label
            }));
            setPriorityOptions(priorityOpts);
            console.log('✅ OpportunityForm: Set Priority options:', priorityOpts);
          }
          
          // Update Category options
          if (selectionValues?.Category?.values) {
            const categoryOpts = selectionValues.Category.values.map(v => ({
              value: v.value,
              label: v.label
            }));
            setCategoryOptions(categoryOpts);
            console.log('✅ OpportunityForm: Set Category options:', categoryOpts);
          }
          
          // Update Currency options
          if (selectionValues?.Currency?.values) {
            const currencyOpts = selectionValues.Currency.values.map(v => ({
              value: v.value,
              label: v.label
            }));
            setCurrencyOptions(currencyOpts);
            console.log('✅ OpportunityForm: Set Currency options:', currencyOpts);
          }
        } else {
          console.error('❌ OpportunityForm: Failed to fetch selection values, status:', response.status);
          // Fallback to default values
          setDefaultOptions();
        }
      } catch (error) {
        console.error('❌ OpportunityForm: Error loading selection values:', error);
        // Fallback to default values
        setDefaultOptions();
      }
    };

    const setDefaultOptions = () => {
      setStageOptions([
        { value: 'Qualified', label: t('opportunities.stageQualified', 'Qualified') },
        { value: 'Proposal', label: t('opportunities.stageProposal', 'Proposal') },
        { value: 'Negotiation', label: t('opportunities.stageNegotiation', 'Negotiation') },
        { value: 'Closed Won', label: t('opportunities.stageClosedWon', 'Closed Won') },
        { value: 'Closed Lost', label: t('opportunities.stageClosedLost', 'Closed Lost') },
      ]);
      setPriorityOptions([
        { value: 'Low', label: t('opportunities.priorityLow', 'Low') },
        { value: 'Medium', label: t('opportunities.priorityMedium', 'Medium') },
        { value: 'High', label: t('opportunities.priorityHigh', 'High') },
        { value: 'Critical', label: t('opportunities.priorityCritical', 'Critical') },
      ]);
      setCategoryOptions([
        { value: 'New Business', label: t('opportunities.categoryNewBusiness', 'New Business') },
        { value: 'Existing Business', label: t('opportunities.categoryExistingBusiness', 'Existing Business') },
        { value: 'Renewal', label: t('opportunities.categoryRenewal', 'Renewal') },
        { value: 'Upsell', label: t('opportunities.categoryUpsell', 'Upsell') },
      ]);
    };
    
    loadSelectionValues();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update form data when opportunity changes (for edit mode)
  React.useEffect(() => {
    if (opportunity) {
      const salespersonID = opportunity.SalespersonID ? String(opportunity.SalespersonID) : '';
      setFormData({
        // Basic Information
        OpportunityName: opportunity.OpportunityName || '',
        Description: opportunity.Description || '',
        Stage: opportunity.Stage || 'Qualified',
        Priority: opportunity.Priority || 'Medium',
        Category: opportunity.Category || 'New Business',
        
        // Required database fields
        SalespersonID: salespersonID,
        
        // Customer Information
        CustomerName: opportunity.CustomerName || '',
        ContactName: opportunity.ContactName || '',
        ContactEmail: opportunity.ContactEmail || '',
        ContactPhone: opportunity.ContactPhone || '',
        
        // Financial Information
        Value: opportunity.Value || '',
        Currency: opportunity.Currency || 'THB',
        Probability: opportunity.Probability || 50,
        
        // Timeline
        ExpectedCloseDate: formatDateForInput(opportunity.ExpectedCloseDate),
        LastActivityDate: formatDateForInput(opportunity.LastActivityDate),
        NextFollowUpDate: formatDateForInput(opportunity.NextFollowUpDate),
        
        // Additional Information
        CompetitorInfo: opportunity.CompetitorInfo || '',
        Requirements: opportunity.Requirements || '',
        Notes: opportunity.Notes || '',
        
        // Sales Information
        NextStepAction: opportunity.NextStepAction || '',
        
        // Flags
        IsActive: opportunity.IsActive !== undefined ? opportunity.IsActive : true,
        IsHighValue: opportunity.IsHighValue || false,
      });
      setAttributes(parseAttributes(opportunity.Attributes || opportunity.attributes));
      setOriginalSalespersonID(salespersonID);
    }
  }, [opportunity]);

  React.useEffect(() => {
    if (!opportunity && mode === 'create') {
      setAttributes({});
    }
  }, [opportunity, mode]);

  // Load hidden fields from entity config
  React.useEffect(() => {
    if (entityConfig) {
      // Extract hidden fields from config
      // The entityConfig returned from useEntityConfig hook already has hiddenFields at root level
      const configHiddenFields = entityConfig.hiddenFields || [];
      if (Array.isArray(configHiddenFields)) {
        setHiddenFields(configHiddenFields);
        console.log('✅ OpportunityForm: Loaded hidden fields from config:', configHiddenFields);
      } else {
        console.warn('⚠️ OpportunityForm: hiddenFields is not an array:', configHiddenFields);
        setHiddenFields([]);
      }
    }
  }, [entityConfig]);

  React.useEffect(() => {
    if (!entityConfig) {
      return;
    }

    try {
      const extracted = extractCustomAttributeFields(entityConfig, {
        tenantCode,
        includeGlobal: true
      });
      setCustomAttributeFields(extracted);
    } catch (err) {
      console.error('OpportunityForm: Failed to extract custom attribute fields', err);
      setCustomAttributeFields([]);
    }
  }, [entityConfig, tenantCode]);

  React.useEffect(() => {
    if (customAttributeFields.length === 0) return;
    setAttributes((prev) => {
      const next = { ...prev };
      let changed = false;
      customAttributeFields.forEach((field) => {
        if (!(field.name in next)) {
          next[field.name] = '';
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [customAttributeFields]);

  // Load unified entity relations in edit mode
  React.useEffect(() => {
    const loadRelations = async () => {
      const id = opportunity?.OpportunityID;
      if (!id) return;
      try {
        const relationsResp = await entityRelationService.getRelatedEntities('Opportunity', Number(id), { direction: 'both', timeoutMs: 10000 });
        const relations = Array.isArray(relationsResp) ? relationsResp : (relationsResp?.relations || []);
        const mapped = relations.map(rel => {
          const srcType = rel.SourceEntityType || rel.sourceEntityType;
          const tgtType = rel.TargetEntityType || rel.targetEntityType;
          const srcId = rel.SourceEntityID || rel.sourceEntityID;
          const tgtId = rel.TargetEntityID || rel.targetEntityID;
          const isOpportunitySource = (srcType || '').toString().toLowerCase() === 'opportunity';
          const entityType = (isOpportunitySource ? tgtType : srcType) || '';
          const entityId = isOpportunitySource ? tgtId : srcId;
          return {
            EntityType: entityType,
            EntityID: entityId,
            RelationID: rel.RelationID || rel.relationId,
            RelationType: rel.RelationType || rel.relationType
          };
        });
        setEntityRelationsUnified(mapped);
      } catch {
        setEntityRelationsUnified([]);
      }
    };
    loadRelations();
  }, [opportunity?.OpportunityID]);

  // Helpers copied from LeadForm but adapted for Opportunity
  const getEntityId = (entity, entityType) => {
    switch (entityType) {
      case 'Customer': return entity.CustomerID;
      case 'Lead': return entity.LeadID;
      case 'Activity': return entity.ActivityID;
      case 'SaleNote': return entity.SaleNoteID || entity.NoteID;
      default: return null;
    }
  };

  const getEntityName = (entity, entityType) => {
    switch (entityType) {
      case 'Customer': {
        const personName = (entity.CustomerName && entity.CustomerName.trim())
          || `${(entity.FirstName || '').trim()} ${(entity.LastName || '').trim()}`.trim();
        return personName || entity.CompanyName || `Customer ${entity.CustomerID}`;
      }
      case 'Lead': return entity.Title || entity.ContactName || entity.CompanyName || `Lead ${entity.LeadID}`;
      case 'Activity': return entity.Subject || `${entity.ActivityType || 'Activity'} ${entity.ActivityID}`;
      case 'SaleNote': {
        const noteText = entity.NoteText || '';
        const firstLine = noteText.split('\n')[0] || noteText;
        const previewText = firstLine.length > 100 ? firstLine.substring(0, 100).trim() + '...' : firstLine.trim();
        return previewText || `Sale Note ${entity.NoteID || entity.SaleNoteID}`;
      }
      default: return `${entityType}`;
    }
  };

  const getEntitySubInfo = (entity, entityType) => {
    switch (entityType) {
      case 'Customer':
        return entity.CompanyName || entity.Email || entity.Phone || '';
      case 'Lead':
        return entity.CompanyName || entity.ContactEmail || entity.ContactPhone || '';
      case 'Activity':
        return entity.ActivityType || entity.Status || '';
      case 'SaleNote':
        if (entity.NoteDate) {
          try {
            return new Date(entity.NoteDate).toLocaleDateString();
          } catch (_) {
            return '';
          }
        }
        return '';
      default:
        return '';
    }
  };

  // Load existing relations for Opportunity (edit mode)
  React.useEffect(() => {
    const loadRelations = async () => {
      if (!(mode === 'edit' && opportunity && opportunity.OpportunityID)) return;
      try {
        const relResp = await entityRelationService.getRelatedEntities('Opportunity', opportunity.OpportunityID, { direction: 'both', timeoutMs: 12000 });
        const mapped = (relResp.relations || []).map(rel => {
          const srcType = rel.SourceEntityType || rel.sourceEntityType;
          const tgtType = rel.TargetEntityType || rel.targetEntityType;
          const srcId = rel.SourceEntityID || rel.sourceEntityID;
          const tgtId = rel.TargetEntityID || rel.targetEntityID;
          const isOppSource = (srcType || '').toLowerCase() === 'opportunity';
          const type = isOppSource ? tgtType : srcType;
          const id = isOppSource ? tgtId : srcId;
          return { type, id, name: '' };
        });
        initialRelationKeysRef.current = new Set(mapped.map(e => `${e.type}:${e.id}`));
        const enriched = await Promise.all(mapped.map(async (e) => {
          try {
            let endpoint = '';
            if (e.type === 'Customer') endpoint = `/customers/${e.id}`;
            else if (e.type === 'Lead') endpoint = `/leads/${e.id}`;
            else if (e.type === 'Activity') endpoint = `/activities/${e.id}`;
            else if (e.type === 'SaleNote') endpoint = `/sales-notes/${e.id}`;
            if (!endpoint) return { ...e, name: `${e.type} ${e.id}` };
            const data = await api.get(endpoint);
            const entity = data.customer || data.lead || data.activity || data.saleNote || data.salesNote || data;
            return { ...e, name: getEntityName(entity, e.type) };
          } catch {
            return { ...e, name: `${e.type} ${e.id}` };
          }
        }));
        setRelatedEntities(enriched);
      } catch (err) {
        console.warn('OpportunityForm: Failed to load existing relations', err);
      }
    };
    loadRelations();
  }, [mode, opportunity]);

  const handleAddCustomAttributeField = async ({
    fieldName,
    label,
    type,
    options: choiceOptions = []
  }) => {
    if (!tenantCode) {
      const errorMessage = 'Tenant context is required to add custom fields.';
      console.error(`❌ OpportunityForm: ${errorMessage}`);
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }

    const orderBase = 900 + customAttributeFields.length * 5;
    const isChoiceField = type === 'choice';
    const dataType =
      type === 'number' ? 'decimal' : type === 'date' ? 'date' : 'string';
    const timestamp = new Date().toISOString();

    if (isChoiceField && choiceOptions.length === 0) {
      const message = 'At least one option is required for choice fields.';
      console.error(`❌ OpportunityForm: ${message}`);
      toast.error(message);
      throw new Error(message);
    }

    const slugifyOptionValue = (inputValue = '', fallbackIndex = 0) => {
      const normalised = inputValue
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_|_$/g, '');

      if (normalised) return normalised;
      return `option_${fallbackIndex + 1}`;
    };

    const seenOptionValues = new Set();
    const normalizedOptions = isChoiceField
      ? choiceOptions.map((option, index) => {
          let baseValue = slugifyOptionValue(
            option?.value ?? option?.label ?? '',
            index
          );
          let uniqueValue = baseValue;
          let counter = 1;

          while (seenOptionValues.has(uniqueValue)) {
            counter += 1;
            uniqueValue = slugifyOptionValue(
              `${baseValue}_${counter}`,
              index + counter
            );
          }

          seenOptionValues.add(uniqueValue);

          return {
            value: uniqueValue,
            label: option?.label ?? uniqueValue,
            ...(option?.labelTH ? { labelTH: option.labelTH } : {})
          };
        })
      : [];

    const fieldConfig = {
      displayName: label,
      displayNameTH: label,
      description: `Custom attribute "${label}"`,
      dataType,
      metadata: {
        isCustomAttribute: true,
        attributeType: isChoiceField ? 'choice' : type,
        attributeSource: 'attributes',
        createdAt: timestamp,
        tenantCode,
        tenantOnly: true
      },
      storage: {
        type: 'attributes',
        path: fieldName
      },
      validation: {
        required: false,
        ...(isChoiceField
          ? { enum: normalizedOptions.map((option) => option.value) }
          : {})
      },
      ui: {
        inputType: isChoiceField ? 'select' : type,
        showInForm: true,
        showInDetail: true,
        showInList: false,
        showInExport: false,
        group: 'Opportunity Additional Data',
        order: orderBase,
        ...(isChoiceField ? { options: normalizedOptions } : {})
      },
      _source: 'tenant_custom',
      _source_tenant: tenantCode,
      _created_at: timestamp,
      _tenantSpecific: true,
      _tenantCode: tenantCode
    };

    const response = await fetch('/api/tenant-entity-config/opportunity/custom-field', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      },
      body: JSON.stringify({
        fieldName,
        fieldConfig
      })
    });

    let responseBody = null;
    if (!response.ok) {
      responseBody = await response.json().catch(() => ({}));
      console.error('❌ OpportunityForm: Failed to add custom field:', responseBody);
      const message = responseBody?.message || 'Failed to add custom field';
      toast.error(message);
      throw new Error(message);
    } else {
      responseBody = await response.json().catch(() => ({}));
    }

    clearConfigCache('opportunity');
    const updatedConfig = await loadEntityConfig('opportunity', tenantCode);
    const updatedFields = extractCustomAttributeFields(
      updatedConfig || entityConfig || {},
      {
        tenantCode,
        includeGlobal: true
      }
    );
    setCustomAttributeFields(updatedFields);
    setAttributes((prev) => {
      const next = { ...prev };
      updatedFields.forEach((field) => {
        if (!(field.name in next)) {
          next[field.name] = '';
        }
      });
      return next;
    });
    toast.success(t('customAttributes.fieldAdded', 'Custom field added'));
    return responseBody;
  };

  const openEntitySelectionModal = (entityType, currentSearchTerm = '') => {
    const alreadySelectedIds = relatedEntities
      .filter(e => e.type === entityType)
      .map(e => e.id.toString());
    setEntitySelectionModal({ isOpen: true, entityType, searchTerm: currentSearchTerm, results: [], loading: true, selectedIds: alreadySelectedIds });
    performEntitySearchForModal(currentSearchTerm || '', entityType);
  };

  const closeEntitySelectionModal = (addSelected = false) => {
    if (addSelected && entitySelectionModal.entityType) {
      const newEntities = [];
      entitySelectionModal.results.forEach(entity => {
        const id = getEntityId(entity, entitySelectionModal.entityType);
        const idStr = id?.toString();
        if (!idStr) return;
        const isSelected = entitySelectionModal.selectedIds.includes(idStr);
        const already = relatedEntities.some(e => e.type === entitySelectionModal.entityType && e.id.toString() === idStr);
        if (isSelected && !already) {
          newEntities.push({ type: entitySelectionModal.entityType, id, name: getEntityName(entity, entitySelectionModal.entityType) });
        }
      });
      const remainingIds = new Set(entitySelectionModal.selectedIds);
      const kept = relatedEntities.filter(e => e.type !== entitySelectionModal.entityType || remainingIds.has(e.id.toString()));
      setRelatedEntities([...kept, ...newEntities]);
    }
    setEntitySelectionModal({ isOpen: false, entityType: null, searchTerm: '', results: [], loading: false, selectedIds: [] });
  };

  const toggleEntitySelectionInModal = (entity, entityType) => {
    const idStr = getEntityId(entity, entityType)?.toString();
    if (!idStr) return;
    setEntitySelectionModal(prev => {
      const isSelected = prev.selectedIds.includes(idStr);
      return { ...prev, selectedIds: isSelected ? prev.selectedIds.filter(x => x !== idStr) : [...prev.selectedIds, idStr] };
    });
  };

  const handleEntityModalSearchChange = (value) => {
    setEntitySelectionModal(prev => ({ ...prev, searchTerm: value }));
    const entityType = entitySelectionModal.entityType;
    if (!entityType) return;
    const key = `modal-${entityType}`;
    if (searchTimeouts[key]) clearTimeout(searchTimeouts[key]);
    const timeoutId = setTimeout(() => performEntitySearchForModal(value, entityType), 300);
    setSearchTimeouts(prev => ({ ...prev, [key]: timeoutId }));
  };

  const performEntitySearchForModal = async (term, entityType) => {
    try {
      let endpoint = '';
      switch (entityType) {
        case 'Customer':
          endpoint = !term || term.trim() === '' ? `/api/customers?limit=10` : `/api/customers?search=${encodeURIComponent(term)}&limit=15`;
          break;
        case 'Lead':
          endpoint = !term || term.trim() === '' ? `/api/leads?limit=10` : `/api/leads?search=${encodeURIComponent(term)}&limit=15`;
          break;
        case 'Activity':
          endpoint = !term || term.trim() === '' ? `/api/activities?limit=10` : `/api/activities?search=${encodeURIComponent(term)}&limit=15`;
          break;
        case 'SaleNote':
          endpoint = !term || term.trim() === '' ? `/api/sales-notes?limit=10` : `/api/sales-notes?search=${encodeURIComponent(term)}&limit=15`;
          break;
        default:
          return;
      }
      const response = await fetch(endpoint, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } });
      if (response.ok) {
        const data = await response.json();
        let results = [];
        const key = entityType.toLowerCase() + 's';
        if (data[key]) results = data[key];
        else if (data.activities) results = data.activities;
        else if (data.saleNotes) results = data.saleNotes;
        else if (data.salesNotes) results = data.salesNotes;
        else if (data.data && Array.isArray(data.data)) results = data.data;
        else if (Array.isArray(data)) results = data;
        setEntitySelectionModal(prev => ({ ...prev, results, loading: false }));
      } else {
        setEntitySelectionModal(prev => ({ ...prev, results: [], loading: false }));
      }
    } catch (err) {
      setEntitySelectionModal(prev => ({ ...prev, results: [], loading: false }));
    }
  };

  // addEntityRelation removed; selection modal handles adding and will be synced on save

  const validate = () => {
    const errs = {};
    
    // Required fields
    if (isEmpty(formData.OpportunityName)) {
      errs.OpportunityName = 'กรุณากรอกชื่อโอกาสทางธุรกิจ / Please enter opportunity name';
    }
    
    if (!formData.SalespersonID) {
      errs.SalespersonID = 'กรุณาเลือกพนักงานขาย / Please select a salesperson';
    }
    
    // Value validation
    if (!formData.Value || formData.Value <= 0) {
      errs.Value = 'กรุณากรอกมูลค่าที่มากกว่า 0 / Please enter a value greater than 0';
    }
    
    // Probability validation (0-100)
    if (!isEmpty(formData.Probability) && !isInRange(formData.Probability, 0, 100)) {
      errs.Probability = 'ความน่าจะเป็นต้องอยู่ระหว่าง 0-100% / Probability must be between 0-100%';
    }
    
    // Date validations
    if (!isEmpty(formData.ExpectedCloseDate) && !isEmpty(formData.LastActivityDate)) {
      if (new Date(formData.ExpectedCloseDate) < new Date(formData.LastActivityDate)) {
        errs.ExpectedCloseDate = 'วันปิดดีลต้องอยู่หลังวันกิจกรรมล่าสุด / Close date must be after last activity date';
      }
    }
    
    if (!isEmpty(formData.NextFollowUpDate) && !isEmpty(formData.ExpectedCloseDate)) {
      if (new Date(formData.NextFollowUpDate) > new Date(formData.ExpectedCloseDate)) {
        errs.NextFollowUpDate = 'วันติดตามต้องอยู่ก่อนวันปิดดีล / Follow-up date must be before close date';
      }
    }
    
    // Email validation
    if (!isEmpty(formData.ContactEmail) && !isValidEmail(formData.ContactEmail)) {
      errs.ContactEmail = 'รูปแบบอีเมลไม่ถูกต้อง / Invalid email format';
    }
    
    setErrors(errs);
    
    // Scroll to first error
    if (Object.keys(errs).length > 0) {
      scrollToFirstError(errs);
    }
    
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    
    try {
      // Clean up the data before sending
      const sanitizedAttributes = sanitizeAttributePayload(
        attributes,
        customAttributeFields
      );
      
      // Map form fields to API field names (create clean object to avoid duplicates)
      const submitData = {
        // Attributes
        attributes: sanitizedAttributes,
        Attributes: sanitizedAttributes,
        
        // Basic Information
        OpportunityName: formData.OpportunityName,
        opportunityName: formData.OpportunityName,
        Description: formData.Description,
        description: formData.Description,
        Stage: formData.Stage,
        stage: formData.Stage,
        Priority: formData.Priority,
        priority: formData.Priority,
        Category: formData.Category,
        category: formData.Category,
        
        // Salesperson
        SalespersonID: formData.SalespersonID,
        salespersonId: formData.SalespersonID,
        
        // Customer Information
        CustomerName: formData.CustomerName,
        customerName: formData.CustomerName,
        ContactName: formData.ContactName,
        contactName: formData.ContactName,
        ContactEmail: normalizeEmailList(formData.ContactEmail),
        contactEmail: normalizeEmailList(formData.ContactEmail),
        ContactPhone: formData.ContactPhone,
        contactPhone: formData.ContactPhone,
        
        // Financial Information
        Value: formData.Value,
        estimatedValue: formData.Value,
        Currency: formData.Currency,
        currency: formData.Currency,
        Probability: formData.Probability,
        probability: formData.Probability,
        
        // Timeline
        ExpectedCloseDate: formData.ExpectedCloseDate || null,
        expectedCloseDate: formData.ExpectedCloseDate || null,
        LastActivityDate: formData.LastActivityDate || null,
        lastActivityDate: formData.LastActivityDate || null,
        NextFollowUpDate: formData.NextFollowUpDate || null,
        nextFollowUpDate: formData.NextFollowUpDate || null,
        
        // Additional Information (Critical: These need to be sent correctly)
        CompetitorInfo: formData.CompetitorInfo || null,
        competitorInfo: formData.CompetitorInfo || null,
        Requirements: formData.Requirements || null,
        requirements: formData.Requirements || null,
        NextStepAction: formData.NextStepAction || null,
        nextStepAction: formData.NextStepAction || null,
        Notes: formData.Notes || null,
        notes: formData.Notes || null,
        
        // Flags
        IsActive: formData.IsActive,
        isActive: formData.IsActive,
        IsHighValue: formData.IsHighValue,
        isHighValue: formData.IsHighValue,
        
        // IDs
        customerId: null,
        leadId: formData.LeadID || null
      };
      
      // Convert empty strings to null for optional fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });
      
      // Convert numeric fields
      if (submitData.estimatedValue) {
        submitData.estimatedValue = parseFloat(submitData.estimatedValue);
      }
      if (submitData.Probability) {
        submitData.Probability = parseInt(submitData.Probability);
      }
      
      console.log('Submitting opportunity data:', submitData);
      
      // Default handler if onSubmit prop is not provided
      let submitResult;
      if (onSubmit) {
        submitResult = await onSubmit(submitData);
      } else {
        // Default implementation: create or update via API
        if (mode === 'edit' && (opportunity?.OpportunityID || id)) {
          const opportunityId = opportunity?.OpportunityID || id;
          const response = await api.put(`/opportunities/${opportunityId}`, submitData);
          submitResult = response.opportunity || response;
          toast.success(t('opportunities.updateSuccess', 'Opportunity updated successfully'));
          // Navigate to detail page after update
          navigate(`/opportunities/${opportunityId}`);
        } else {
          const response = await api.post('/opportunities', submitData);
          submitResult = response.opportunity || response;
          const newOpportunityId = response.opportunityId || response.opportunity?.OpportunityID || response.OpportunityID;
          toast.success(t('opportunities.createSuccess', 'Opportunity created successfully'));
          // Navigate to detail page after create
          if (newOpportunityId) {
            navigate(`/opportunities/${newOpportunityId}`);
          } else {
            navigate('/opportunities');
          }
        }
      }
      
      setSubmitSuccess(true);
      
      // Auto-close success message after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
      // After save, sync relationships if OpportunityID is available
      let opportunityId = opportunity?.OpportunityID;
      if (!opportunityId && submitResult) {
        opportunityId = submitResult?.opportunity?.OpportunityID || submitResult?.OpportunityID || submitResult?.id || submitResult?.opportunityId;
      }
      if (opportunityId) {
        try {
          const existingKeys = new Set(initialRelationKeysRef.current ? Array.from(initialRelationKeysRef.current) : []);
          const selectedKeys = new Set(relatedEntities.map(e => `${e.type}:${e.id}`));
          const toCreate = relatedEntities.filter(e => !existingKeys.has(`${e.type}:${e.id}`));
          const createPromises = toCreate.map(e => entityRelationService.create({
            sourceEntityType: 'Opportunity',
            sourceEntityID: Number(opportunityId),
            targetEntityType: e.type,
            targetEntityID: e.id,
            relationType: 'related_to'
          }));

          let deletePromises = [];
          if (initialRelationKeysRef.current && initialRelationKeysRef.current.size > 0) {
            try {
              const existing = await entityRelationService.getRelatedEntities('Opportunity', Number(opportunityId), { direction: 'both' });
              const relations = existing.relations || [];
              const relationsToDelete = relations.filter(rel => {
                const srcType = rel.SourceEntityType || rel.sourceEntityType || '';
                const tgtType = rel.TargetEntityType || rel.targetEntityType || '';
                const srcId = rel.SourceEntityID || rel.sourceEntityID;
                const tgtId = rel.TargetEntityID || rel.targetEntityID;
                const isOppSource = srcType.toLowerCase() === 'opportunity';
                const entityType = isOppSource ? tgtType : srcType;
                const entityId = isOppSource ? tgtId : srcId;
                const key = `${entityType}:${entityId}`;
                return !selectedKeys.has(key);
              });
              if (relationsToDelete.length > 0) {
                const relationIDsToDelete = relationsToDelete.map(rel => rel.RelationID || rel.relationID).filter(Boolean);
                if (relationIDsToDelete.length > 0) {
                  deletePromises = [
                    entityRelationService.deleteBatch(relationIDsToDelete, 5000)
                  ];
                }
              }
            } catch (e) {
              console.warn('OpportunityForm: Failed to refetch relations for deletions', e);
            }
          }

          const createResults = await Promise.allSettled(createPromises);
          const deleteResults = deletePromises.length > 0 ? await Promise.allSettled(deletePromises) : [];
          const createFailed = createResults.filter(r => r.status === 'rejected');
          let deleteFailed = [];
          if (deleteResults.length > 0 && deleteResults[0].status === 'fulfilled') {
            const batchResult = deleteResults[0].value;
            deleteFailed = batchResult.results ? batchResult.results.filter(r => !r.success) : [];
          } else if (deleteResults.length > 0 && deleteResults[0].status === 'rejected') {
            deleteFailed = [{ error: deleteResults[0].reason?.message || 'Batch delete failed' }];
          }
          const totalFailed = createFailed.length + deleteFailed.length;
          if (totalFailed > 0) {
            alert(t('common.partialRelationFailure', 'Saved, but some relationship operations failed.')); 
          }
        } catch (relErr) {
          console.error('OpportunityForm: Relationship sync failed', relErr);
          alert(t('common.relationSyncFailed', 'Saved, but relationships could not be updated.'));
        }
      }
      
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitError(err.message || t('opportunities.submitError', 'Failed to save opportunity. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Check for salesperson change warning
    if (field === 'SalespersonID' && mode === 'edit') {
      const newSalespersonID = String(value || '');
      
      // Set original salesperson ID if not set yet
      if (!originalSalespersonID && newSalespersonID) {
        setOriginalSalespersonID(newSalespersonID);
        return; // Don't show warning on initial load
      }
      
      if (originalSalespersonID) {
        const hasChanged = newSalespersonID !== originalSalespersonID && newSalespersonID !== '';
        const currentUserId = String(user?.tenantUserId || user?.userId || user?.UserID || '');
        
        // Show warning if policy is 1 or 2 and user is changing salesperson to someone else
        if (hasChanged && dataGovernancePolicy) {
          const policyType = Number(dataGovernancePolicy.policyType || dataGovernancePolicy.PolicyType || 0);
          const isPolicy1Or2 = policyType === 1 || policyType === 2;
          const isChangingToOther = newSalespersonID !== currentUserId;
          
          console.log('🔍 OpportunityForm: Salesperson change check:', {
            originalSalespersonID,
            newSalespersonID,
            currentUserId,
            policyType,
            isPolicy1Or2,
            isChangingToOther,
            dataGovernancePolicy
          });
          
          if (isPolicy1Or2 && isChangingToOther) {
            setShowSalespersonWarning(true);
          } else {
            setShowSalespersonWarning(false);
          }
        } else {
          setShowSalespersonWarning(false);
        }
      }
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Dynamic list values are now loaded via useState and useEffect above
  // Helpers for styling/preview to match OpportunityDetail layout
  const getStageColor = (stage) => {
    switch (stage) {
      case 'Lead': return 'bg-gray-100 text-gray-800';
      case 'Qualified': return 'bg-teal-100 text-teal-800';
      case 'Proposal': return 'bg-yellow-100 text-yellow-800';
      case 'Negotiation': return 'bg-orange-100 text-orange-800';
      case 'Closed Won': return 'bg-green-100 text-green-800';
      case 'Closed Lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProbabilityColor = (probability) => {
    if (probability >= 80) return 'text-green-600';
    if (probability >= 60) return 'text-yellow-600';
    if (probability >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  // Helper function to check if field is hidden
  const isFieldHidden = (fieldName) => {
    return hiddenFields.includes(fieldName);
  };

  const getStageIcon = (stage) => {
    switch (stage) {
      case 'Lead': return Target;
      case 'Qualified': return CheckCircle;
      case 'Proposal': return FileText;
      case 'Negotiation': return TrendingUp;
      case 'Closed Won': return Trophy;
      case 'Closed Lost': return AlertCircle;
      default: return Activity;
    }
  };

  const StageIcon = getStageIcon(formData?.Stage);


  // Default onCancel handler
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      // Navigate back to opportunities list or detail page
      if (mode === 'edit' && (opportunity?.OpportunityID || id)) {
        const opportunityId = opportunity?.OpportunityID || id;
        navigate(`/opportunities/${opportunityId}`);
      } else {
        navigate('/opportunities');
      }
    }
  };

  // Show loading state while loading opportunity data
  if (loadingOpportunity) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-teal-600 dark:text-teal-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('opportunities.loading', 'Loading opportunity...')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <EntitySelectionModal
        isOpen={entitySelectionModal.isOpen}
        entityType={entitySelectionModal.entityType}
        title={`Select ${entitySelectionModal.entityType || 'Entity'}`}
        subtitle={entitySelectionModal.selectedIds.length > 0 ? `${entitySelectionModal.selectedIds.length} ${t('activities.selected', 'selected')}` : undefined}
        searchTerm={entitySelectionModal.searchTerm}
        onSearchTermChange={handleEntityModalSearchChange}
        searchPlaceholder={t('activities.searchEntity', { entityType: entitySelectionModal.entityType }) || `Search ${entitySelectionModal.entityType || ''}`}
        loading={entitySelectionModal.loading}
        results={entitySelectionModal.results}
        selectedIds={entitySelectionModal.selectedIds}
        onToggleEntity={toggleEntitySelectionInModal}
        onClose={() => closeEntitySelectionModal(false)}
        onConfirm={() => closeEntitySelectionModal(true)}
        confirmLabel={
          entitySelectionModal.selectedIds.length > 0
            ? `${t('common.add', 'Add')} (${entitySelectionModal.selectedIds.length})`
            : t('common.add', 'Add')
        }
        confirmIcon={Plus}
        confirmDisabled={entitySelectionModal.selectedIds.length === 0}
        cancelLabel={t('common.cancel', 'Cancel')}
        emptyMessage={t('activities.noResultsFound', 'No results found')}
        idleMessage={t('activities.startTypingToSearch', 'Start typing to search')}
        loadingMessage={t('activities.searching', 'Searching...')}
        resolveId={(entity, type) => getEntityId(entity, type || entitySelectionModal.entityType)}
        resolveName={(entity, type) => getEntityName(entity, type || entitySelectionModal.entityType)}
        resolveSubInfo={(entity, type) => getEntitySubInfo(entity, type || entitySelectionModal.entityType)}
      />

      <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-4 w-4 dark:text-gray-300" />
          </button>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${getStageColor(formData.Stage)}`}>
              <StageIcon className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {mode === 'create' ? t('opportunities.createOpportunity') : t('opportunities.editOpportunity')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {mode === 'create' 
                  ? t('opportunities.createSubtitle', 'Add a new opportunity to track potential sales') 
                  : t('opportunities.editSubtitle', 'Update opportunity information')}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setShowHelpModal(true)}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
            title="ดูคู่มือการใช้งาน"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>{loading ? t('common.saving') : t('common.save')}</span>
        </button>
        </div>
      </div>

      {/* Status and Priority Badges */}
      <div className="flex items-center space-x-2 mb-6">
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStageColor(formData.Stage)}`}>
          {formData.Stage || '—'}
        </span>
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getPriorityColor(formData.Priority)}`}>
          {formData.Priority || 'Not Set'}
        </span>
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
          formData.IsActive ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
        }`}>
          {formData.IsActive ? 'Active' : 'Inactive'}
        </span>
        {formData.IsHighValue && (
          <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
            High Value
          </span>
        )}
      </div>


      {/* Error/Success Messages */}
      {submitError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <X className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{submitError}</p>
          </div>
        </div>
      )}
      
      {submitSuccess && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400 mr-2" />
            <p className="text-green-800 dark:text-green-300">{t('opportunities.submitSuccess', 'Opportunity saved successfully!')}</p>
          </div>
        </div>
      )}

      {/* Layout: Main form and Sidebar summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
        {/* Validation Summary */}
        <ValidationSummary 
          errors={errors} 
          onFieldClick={(field) => {
            const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.focus?.();
            }
          }}
        />
        
        {/* Basic Information */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-4">
            <Trophy className="h-5 w-5 text-blue-500 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('opportunities.basicInfo')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!isFieldHidden('OpportunityName') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.opportunityName', 'Opportunity Name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="OpportunityName"
                  name="OpportunityName"
                  value={formData.OpportunityName}
                  onChange={(e) => handleFieldChange('OpportunityName', e.target.value)}
                  className={getInputClassName(!!errors.OpportunityName)}
                  placeholder={t('opportunities.opportunityNamePlaceholder', 'Enter opportunity name')}
                />
                <FieldError error={errors.OpportunityName} />
              </div>
            )}

            {!isFieldHidden('Stage') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.stage')}
                </label>
                <select
                  value={formData.Stage}
                  onChange={(e) => handleFieldChange('Stage', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {stageOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}

            {!isFieldHidden('Priority') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.priority')}
                </label>
                <select
                  value={formData.Priority}
                  onChange={(e) => handleFieldChange('Priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}

            {!isFieldHidden('Category') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.category')}
                </label>
                <select
                  value={formData.Category}
                  onChange={(e) => handleFieldChange('Category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {categoryOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('opportunities.salesperson', 'Salesperson')} <span className="text-red-500">*</span>
              </label>
              <select
                id="SalespersonID"
                name="SalespersonID"
                value={formData.SalespersonID}
                onChange={(e) => handleFieldChange('SalespersonID', e.target.value)}
                className={getSelectClassName(!!errors.SalespersonID)}
              >
                <option value="">{t('opportunities.selectSalesperson', 'Select a salesperson')}</option>
                {salespersons.length === 0 ? (
                  <option value="" disabled>{t('opportunities.noSalespersons', 'No salespersons available')}</option>
                ) : (
                  salespersons.map(salesperson => (
                    <option key={salesperson.UserID} value={String(salesperson.UserID)}>
                      {salesperson.FirstName} {salesperson.LastName}
                    </option>
                  ))
                )}
              </select>
              <FieldError error={errors.SalespersonID} />
              {showSalespersonWarning && (
                <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start space-x-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      {t('opportunities.salespersonChangeWarning', 'Warning: Changing the salesperson')}
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                      {t('opportunities.salespersonChangeWarningDetail', 'Under the current data governance policy, you may no longer be able to view or update this record after saving.')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {!isFieldHidden('Description') && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('opportunities.description')}
              </label>
              <textarea
                value={formData.Description}
                onChange={(e) => handleFieldChange('Description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                placeholder={t('opportunities.descriptionPlaceholder')}
              />
            </div>
          )}
        </div>

        {/* Contact Information */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="h-5 w-5 text-green-500 dark:text-green-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('opportunities.contactInfo', 'Contact Information')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!isFieldHidden('CustomerName') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.customerName')}
                </label>
                {getSuggestionConfig(entityConfig, 'CustomerName') ? (
                  <AutocompleteTextField
                    entityName="opportunity"
                    fieldName="CustomerName"
                    value={formData.CustomerName}
                    onChange={(e) => handleFieldChange('CustomerName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                      errors.CustomerName ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder={t('opportunities.customerNamePlaceholder')}
                    suggestConfig={getSuggestionConfig(entityConfig, 'CustomerName')}
                  />
                ) : (
                  <input
                    type="text"
                    value={formData.CustomerName}
                    onChange={(e) => handleFieldChange('CustomerName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                      errors.CustomerName ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder={t('opportunities.customerNamePlaceholder')}
                  />
                )}
                {errors.CustomerName && <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.CustomerName}</p>}
              </div>
            )}

            {!isFieldHidden('ContactName') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.contactName')}
                </label>
                {getSuggestionConfig(entityConfig, 'ContactName') ? (
                  <AutocompleteTextField
                    entityName="opportunity"
                    fieldName="ContactName"
                    value={formData.ContactName}
                    onChange={(e) => handleFieldChange('ContactName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder={t('opportunities.contactNamePlaceholder')}
                    suggestConfig={getSuggestionConfig(entityConfig, 'ContactName')}
                  />
                ) : (
                  <input
                    type="text"
                    value={formData.ContactName}
                    onChange={(e) => handleFieldChange('ContactName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder={t('opportunities.contactNamePlaceholder')}
                  />
                )}
              </div>
            )}

            {!isFieldHidden('ContactEmail') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.contactEmail')}
                </label>
                <input
                  type="email"
                  id="ContactEmail"
                  name="ContactEmail"
                  value={formData.ContactEmail}
                  onChange={(e) => handleFieldChange('ContactEmail', e.target.value)}
                  onBlur={(e) => handleFieldChange('ContactEmail', normalizeEmailList(e.target.value))}
                  className={getInputClassName(!!errors.ContactEmail)}
                  placeholder={t('opportunities.contactEmailPlaceholder')}
                />
                <FieldError error={errors.ContactEmail} />
              </div>
            )}

            {!isFieldHidden('ContactPhone') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.contactPhone')}
                </label>
                <input
                  type="tel"
                  value={formData.ContactPhone}
                  onChange={(e) => handleFieldChange('ContactPhone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('opportunities.contactPhonePlaceholder')}
                />
              </div>
            )}
          </div>
        </div>

        {/* Financial Information */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-4">
            <DollarSign className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('opportunities.financialInfo')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {!isFieldHidden('Value') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.value')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="Value"
                  name="Value"
                  value={formatNumberWithCommas(formData.Value)}
                  onChange={(e) => {
                    const parsedValue = parseNumberFromFormatted(e.target.value);
                    handleFieldChange('Value', parsedValue);
                  }}
                  onBlur={(e) => {
                    // Ensure value is properly formatted on blur
                    const parsedValue = parseNumberFromFormatted(e.target.value);
                    if (parsedValue !== '') {
                      handleFieldChange('Value', parsedValue);
                    }
                  }}
                  className={getInputClassName(!!errors.Value)}
                  placeholder={t('opportunities.valuePlaceholder')}
                />
                <FieldError error={errors.Value} />
              </div>
            )}

            {!isFieldHidden('Currency') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.currency')}
                </label>
                <select
                  value={formData.Currency}
                  onChange={(e) => handleFieldChange('Currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {currencyOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}

            {!isFieldHidden('Probability') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.probability')} (%)
                </label>
                <input
                  type="number"
                  id="Probability"
                  name="Probability"
                  min="0"
                  max="100"
                  value={formData.Probability}
                  onChange={(e) => handleFieldChange('Probability', e.target.value)}
                  className={getInputClassName(!!errors.Probability)}
                  placeholder="50"
                />
                <FieldError error={errors.Probability} />
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-4">
            <Calendar className="h-5 w-5 text-purple-500 dark:text-purple-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('opportunities.timeline')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {!isFieldHidden('ExpectedCloseDate') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.expectedCloseDate')}
                </label>
                <input
                  type="date"
                  value={formData.ExpectedCloseDate}
                  onChange={(e) => handleFieldChange('ExpectedCloseDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.ExpectedCloseDate ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.ExpectedCloseDate && <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.ExpectedCloseDate}</p>}
              </div>
            )}

            {!isFieldHidden('LastActivityDate') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.lastActivityDate')}
                </label>
                <input
                  type="date"
                  value={formData.LastActivityDate}
                  onChange={(e) => handleFieldChange('LastActivityDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            )}

            {!isFieldHidden('NextFollowUpDate') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.nextFollowUpDate')}
                </label>
                <input
                  type="date"
                  value={formData.NextFollowUpDate}
                  onChange={(e) => handleFieldChange('NextFollowUpDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.NextFollowUpDate ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.NextFollowUpDate && <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.NextFollowUpDate}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Notes & Details */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-4">
            <FileText className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('opportunities.notesAndDetails', 'Notes & Details')}</h2>
          </div>
          
          <div className="space-y-4">
            {!isFieldHidden('Notes') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.notes')}
                </label>
                <textarea
                  value={formData.Notes}
                  onChange={(e) => handleFieldChange('Notes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('opportunities.notesPlaceholder')}
                />
              </div>
            )}

            {!isFieldHidden('Requirements') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.requirements')}
                </label>
                <textarea
                  value={formData.Requirements}
                  onChange={(e) => handleFieldChange('Requirements', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('opportunities.requirementsPlaceholder')}
                />
              </div>
            )}

            {!isFieldHidden('NextStepAction') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.nextStepAction')}
                </label>
                <textarea
                  value={formData.NextStepAction}
                  onChange={(e) => handleFieldChange('NextStepAction', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('opportunities.nextStepActionPlaceholder')}
                />
              </div>
            )}

            {!isFieldHidden('CompetitorInfo') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.competitorInfo')}
                </label>
                <textarea
                  value={formData.CompetitorInfo}
                  onChange={(e) => handleFieldChange('CompetitorInfo', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('opportunities.competitorInfoPlaceholder')}
                />
              </div>
            )}
          </div>
        </div>

      {/* Opportunity Custom Attributes */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <Award className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('opportunities.additionalDataSection', 'Opportunity Additional Data')}
          </h2>
        </div>

        {configLoading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 dark:border-teal-400"></div>
          </div>
        ) : configError ? (
          <p className="text-sm text-red-600 dark:text-red-400">
            {configError}
          </p>
        ) : (
          <CustomAttributesEditor
            fields={customAttributeFields}
            attributes={attributes}
            onChange={(next) => setAttributes(next)}
            onAddField={handleAddCustomAttributeField}
            title={t('opportunities.additionalDataSection', 'Opportunity Additional Data')}
            allExistingFieldNames={entityConfig?.config?.fields ? Object.keys(entityConfig.config.fields) : []}
            entityName="opportunity"
            onFieldsUpdated={async () => {
              // Clear config cache and trigger reload
              clearConfigCache('opportunity');
              window.dispatchEvent(new CustomEvent('entity-config-updated', { 
                detail: { entityName: 'opportunity' } 
              }));
            }}
          />
        )}
      </div>

        {/* Attachments (Edit Mode Only) */}
        {mode === 'edit' && opportunity && opportunity.OpportunityID && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-4">
              <Paperclip className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('opportunities.attachments', 'Attachments')}</h2>
            </div>
            
            <AttachmentServiceV2 
              entityType="Opportunity" 
              entityId={opportunity.OpportunityID} 
              onAttachmentChange={() => {
                // Optional: Add any callback logic here
              }}
            />
          </div>
        )}

        <div className="h-24 md:h-32" />

        <div className="fixed bottom-6 right-6 lg:right-[max(1.5rem,calc((100vw-72rem)/2))] z-40 flex flex-col-reverse gap-3 md:flex-row">
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center space-x-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/80 px-5 py-2 shadow-lg shadow-gray-500/10 dark:shadow-black/30 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 transition-colors backdrop-blur"
          >
            <X className="h-4 w-4" />
            <span>{t('common.cancel')}</span>
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center space-x-2 rounded-full bg-teal-600 dark:bg-teal-500 px-6 py-2 shadow-lg shadow-teal-500/30 dark:shadow-teal-900/30 text-white hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-60 transition-colors"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{loading ? t('common.saving') : t('common.save')}</span>
          </button>
        </div>
        </form>

        {/* Sidebar Summary (live preview) */}
        <div className="space-y-6">
          {/* Entity Relationships - Available in both create and edit modes */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Link className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium dark:text-white">{t('activities.relatedEntities', 'Relationships')}</h3>
              </div>
              {relatedEntities.length > 0 && (
                <span className="text-sm text-gray-500 dark:text-gray-400">{relatedEntities.length} {relatedEntities.length === 1 ? 'entity' : 'entities'} selected</span>
              )}
            </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {entityTypes.map(type => {
                  const count = relatedEntities.filter(e => e.type === type.value).length;
                  const icon = () => {
                    switch (type.value) {
                      case 'Customer': return <Building className="h-5 w-5" />;
                      case 'Lead': return <Target className="h-5 w-5" />;
                      case 'Activity': return <Calendar className="h-5 w-5" />;
                      case 'SaleNote': return <FileText className="h-5 w-5" />;
                      default: return <FileText className="h-5 w-5" />;
                    }
                  };
                  return (
                    <button key={type.value} type="button" onClick={() => openEntitySelectionModal(type.value, '')} className={`group relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 ${count > 0 ? 'border-teal-500 dark:border-teal-400 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-teal-400 dark:hover:border-teal-500 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
                      <div className={`${count > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-teal-500'}`}>{icon()}</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white text-center mt-2">{type.label}</div>
                      {count > 0 && (
                        <div className="absolute -top-2 -right-2 bg-teal-500 dark:bg-teal-600 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">{count}</div>
                      )}
                      <Plus className={`absolute bottom-1 right-1 h-4 w-4 ${count > 0 ? 'opacity-50' : 'opacity-0 group-hover:opacity-100'} text-gray-400 dark:text-gray-500`} />
                    </button>
                  );
                })}
              </div>

              {relatedEntities.length > 0 && (
                <div className="space-y-3">
                  {entityTypes.map(type => {
                    const items = relatedEntities.filter(e => e.type === type.value);
                    if (items.length === 0) return null;
                    const iconSmall = () => {
                      switch (type.value) {
                        case 'Customer': return <Building className="h-4 w-4" />;
                        case 'Lead': return <Target className="h-4 w-4" />;
                        case 'Activity': return <Calendar className="h-4 w-4" />;
                        case 'SaleNote': return <FileText className="h-4 w-4" />;
                        default: return <FileText className="h-4 w-4" />;
                      }
                    };
                    return (
                      <div key={type.value} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          {iconSmall()}
                          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{type.label} ({items.length})</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {items.map((entity, idx) => (
                            <div key={`${entity.type}-${entity.id}-${idx}`} className="group flex items-center space-x-2 px-3 py-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-blue-700 rounded-lg">
                              {iconSmall()}
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{entity.name || `${type.value} ${entity.id}`}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">#{entity.id}</span>
                              <button type="button" onClick={() => setRelatedEntities(prev => prev.filter(e => !(e.type === entity.type && e.id === entity.id)))} className="ml-1 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" title={`Remove ${entity.name}`}>
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                          <button type="button" onClick={() => openEntitySelectionModal(type.value, '')} className="flex items-center space-x-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 text-sm font-medium transition-colors">
                            <Plus className="h-3 w-3" />
                            <span>Add {type.label}</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          
          {/* Stage Progress */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <TrendingUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('opportunities.stageProgress', 'Stage Progress')}</h3>
            </div>
            <div className="text-center">
              <div className={`p-3 rounded-lg ${getStageColor(formData.Stage)}`}>
                <StageIcon className="h-8 w-8 mx-auto mb-2" />
                <p className="font-semibold">{formData.Stage || '—'}</p>
              </div>
              <div className="mt-4">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-teal-600 h-2 rounded-full" 
                    style={{ width: `${formData.Probability || 0}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{formData.Probability || 0}% probability</p>
              </div>
            </div>
          </div>

          {/* Value Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('opportunities.valueInfo', 'Value Information')}</h3>
            </div>
            <div className="space-y-3">
              <div className="text-center">
                <p className={`text-2xl font-bold ${getProbabilityColor(Number(formData.Probability || 0))}`}>
                  {formatNumberWithCommas(formData.Value || 0)} {formData.Currency}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Value</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-lg font-semibold text-teal-600 dark:text-teal-400">{formData.Probability || 0}%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Probability</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                    {(formData.Value && formData.Probability)
                      ? formatNumberWithCommas(((Number(formData.Value) * Number(formData.Probability)) / 100).toFixed(2))
                      : '-'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Expected Value</p>
                </div>
              </div>
            </div>
          </div>

          {/* Priority & Status */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Star className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('opportunities.priorityStatus', 'Priority & Status')}</h3>
            </div>
            <div className="space-y-3">
              <div className="text-center">
                <span className={`inline-flex px-4 py-2 text-lg font-semibold rounded-full border ${getPriorityColor(formData.Priority)}`}>
                  {formData.Priority || 'Not Set'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    formData.IsActive ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {formData.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    formData.IsHighValue ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {formData.IsHighValue ? 'High Value' : 'Standard'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Tags Section */}
          {mode === 'edit' && opportunity?.OpportunityID && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Tag className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('common.tags', 'Tags')}</h3>
              </div>
              <TagService
                entityType="Opportunity"
                entityId={opportunity.OpportunityID}
                initialTags={opportunity.Tags ? (typeof opportunity.Tags === 'string' ? opportunity.Tags.split(',').map(t => t.trim()).filter(Boolean) : opportunity.Tags) : []}
                maxTags={15}
                onChange={async (newTags) => {
                  // Tags updated successfully
                }}
              />
            </div>
          )}
        </div>
      </div>
      <HelpModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
        title="คู่มือการจัดการ Opportunities"
        content={opportunityHelpContent}
      />
      </div>
    </>
  );
};

export default OpportunityForm; 
